import React, { useCallback, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import PropTypes from "prop-types";
import { fetchProjectAdditionalData } from "~/store/actions/actions.projects.jsx";
import { stopwatchDelete, stopwatchEdit } from "~/store/actions/actions.stopwatches.jsx";
import { loadTask } from "~/store/actions/actions.tasks.jsx";
import { getObjectTotalTime, getObjectDirectlyTrackedTime } from "~/store/selectors/selector.additionalObjectData.jsx";
import { getRawConfigOption, getRawPersonalizedConfigOption } from "~/store/selectors/selector.configOptions.jsx";
import { getProjectById } from "~/store/selectors/selector.projects.jsx";
import { getStopwatchById, getStopwatchValue } from "~/store/selectors/selector.stopwatches.jsx";
import { getTaskById } from "~/store/selectors/selector.tasks.jsx";
import { getAllTimeRecords } from "~/store/selectors/selector.timeRecords.jsx";
import { getLoggedUser, getUsersFromProjectMembersForUserSelector } from "~/store/selectors/selector.users.jsx";
import { decimalToMinutes, replaceAvatarSize } from "~/utils/utils.jsx";
import TimeRecordFormBodyContainer from "./TimeRecordFormBodyContainer.jsx";
import TimeRecordFormHeader from "./TimeRecordFormHeader.jsx";
import TimeRecordListGroupedByDate from "./TimeRecordListGroupedByDate.jsx";
import { TimeRecordParentTotalContainer } from "./TimeRecordParentTotalContainer.jsx";
import useAcSelectDate from "../Hooks/useAcSelectDate.jsx";
import { TimeRecordModalContainer } from "../Tracking/TimeRecordModalContainer.jsx";

const TimeRecordTrackedItemModalFormContainer = ({
  handleDismiss,
  parentId,
  parentType,
  projectId,
  stopwatchId,
  disableUserSelection,
  open,
  displaySuccessToast,
}) => {
  const [show, setShow] = useState(false);
  const dispatch = useDispatch();
  const loggedUser = useSelector(getLoggedUser);
  const project = useSelector((state) => getProjectById(state, projectId));
  const task = useSelector((state) => {
    if (parentType !== "Task") {
      return null;
    }
    return getTaskById(state, parentId);
  });
  const trackedItem = useMemo(() => (parentType === "Task" ? task : project), [parentType, project, task]);
  const allTimeRecords = useSelector((state) => getAllTimeRecords(state));
  const users = useSelector((state) => getUsersFromProjectMembersForUserSelector(state, projectId));
  const timeRecordValue = useSelector((state) => getStopwatchValue(state, stopwatchId));
  const stopwatchRoundingInterval = useSelector((state) => getRawConfigOption(state, `rounding_interval`));
  const stopwatchRoundingEnabled = useSelector((state) => getRawConfigOption(state, `rounding_enabled`));
  const keepInStopwatch = useSelector((state) => !!getRawPersonalizedConfigOption(state, "keep_in_stopwatch"));

  const stopwatch = useSelector((state) => getStopwatchById(state, stopwatchId));

  const trackedTime = useSelector((state) => {
    if (parentType === "Task") {
      return getObjectTotalTime(state, parentType, parentId);
    }
    if (parentType === "Project") {
      return getObjectDirectlyTrackedTime(state, parentType, parentId);
    }
    return 0;
  });

  const { dates, formatDates, handleDateChange } = useAcSelectDate();

  const filteredUsers = useMemo(() => {
    if (disableUserSelection) {
      return [];
    }
    return loggedUser.get("class") === "Owner" || project.get("leader_id") === loggedUser.get("id")
      ? users.map((user) => {
          return {
            id: user.id,
            name: user.name,
            image: replaceAvatarSize(user.avatarUrl, 24),
          };
        })
      : [];
  }, [loggedUser, project, users, disableUserSelection]);

  const defaultUser = useMemo(() => {
    return {
      id: loggedUser.get("id"),
      name: loggedUser.get("display_name"),
    };
  }, [loggedUser]);

  const timeRecords = useMemo(() => {
    return allTimeRecords
      .filter(({ is_trashed, parent_type, parent_id }) => {
        if (parentType === "Task") {
          return !is_trashed && parent_type === "Task" && parent_id === parentId;
        }
        return !is_trashed && parent_type === "Project" && parent_id === parentId;
      })
      .map((record) => ({
        id: record.id,
        jobTypeId: record.job_type_id,
        userId: record.user_id,
        value: record.value,
        recordDate:
          typeof record.record_date === "string"
            ? moment.utc(record.record_date).startOf("day").unix()
            : record.record_date,
        parentType: record.parent_type,
        parentId: record.parent_id,
        summary: record.summary,
        billableStatus: record.billable_status,
        createdOn: record.created_on,
        userName: record.user_name,
        userEmail: record.user_email,
      }));
  }, [allTimeRecords, parentId, parentType]);

  const handleOnDismiss = useCallback(
    async (_action, keepInStopwatchValue) => {
      if (stopwatchId) {
        const shouldKeep = keepInStopwatchValue !== undefined ? keepInStopwatchValue : keepInStopwatch;
        if (shouldKeep) {
          dispatch(stopwatchEdit(stopwatchId, { elapsed: 0 }));
        } else {
          dispatch(stopwatchDelete(stopwatchId));
        }
      }
      typeof handleDismiss === "function" && handleDismiss();
    },
    [dispatch, stopwatchId, keepInStopwatch, handleDismiss]
  );

  const total = useMemo(() => {
    return timeRecords.reduce((acc, { value }) => {
      return acc + decimalToMinutes(value);
    }, 0);
  }, [timeRecords]);

  const limitedAccessCount = useMemo(() => {
    const count = decimalToMinutes(trackedTime) - total;
    return count > 0 ? count : 0;
  }, [total, trackedTime]);

  const fetchTrackedItemAfterEditTimeRecord = useCallback(() => {
    if (parentType === "Task") {
      dispatch(loadTask(projectId, parentId, true));
    }
    if (parentType === "Project") {
      dispatch(fetchProjectAdditionalData(parentId));
    }
  }, [dispatch, projectId, parentId, parentType]);

  return (
    <TimeRecordModalContainer
      open={open}
      onClose={handleDismiss}
      bodyClassName="time_record_form_modal_wrapper"
      parentId={parentId}
      parentType={parentType}
      projectId={projectId}
    >
      <div className="time-record-form-modal">
        <TimeRecordFormHeader
          title={trackedItem.name}
          timestamp={dates.to}
          onChange={handleDateChange}
          isProjectTitle={parentType === "Project"}
          projectId={projectId}
          editableDate
        />
        <div className="tw-mb-8 tw-mt-5 tw-border-solid tw-border-0 tw-border-b tw-border-border-primary" />
        <div className="tw-flex tw-justify-between tw-relative">
          <TimeRecordFormBodyContainer
            projectId={project.id}
            isBillable={trackedItem.is_billable}
            onCancel={handleDismiss}
            users={filteredUsers}
            defaultUserId={defaultUser.id}
            defaultUserName={defaultUser.name}
            timeRecordValue={timeRecordValue > 0 ? timeRecordValue : ``}
            stopwatchRoundingInterval={stopwatchRoundingInterval}
            showRoundingInfo={stopwatchRoundingEnabled && stopwatchId > 0}
            source={stopwatchId ? "built_in_timer" : "task_sidebar"}
            recordDate={formatDates.to}
            parentId={parentId}
            parentType={parentType}
            userId={loggedUser.id}
            taskId={parentType === "Task" ? parentId : 0}
            onDismiss={handleOnDismiss}
            descriptionValue={stopwatch?.description ? stopwatch.description : ``}
            displaySuccessToast={displaySuccessToast}
          />
          <TimeRecordParentTotalContainer
            parentId={parentId}
            parentType={parentType}
            onChangeExpand={setShow}
          />
        </div>
        {show ? (
          <TimeRecordListGroupedByDate
            className="tw-mt-8 tw-hidden sm:tw-block"
            timeRecords={timeRecords}
            limitedAccessCount={limitedAccessCount}
            onEdit={fetchTrackedItemAfterEditTimeRecord}
            projectId={projectId}
            parentId={parentId}
          />
        ) : null}
      </div>
    </TimeRecordModalContainer>
  );
};

TimeRecordTrackedItemModalFormContainer.propTypes = {
  projectId: PropTypes.number.isRequired,
  parentId: PropTypes.number.isRequired,
  parentType: PropTypes.oneOf(["Task", "Project"]).isRequired,
  handleDismiss: PropTypes.func,
  stopwatchId: PropTypes.number,
  disableUserSelection: PropTypes.bool,
  open: PropTypes.bool,
  displaySuccessToast: PropTypes.bool,
};

TimeRecordTrackedItemModalFormContainer.defaultProps = {
  stopwatchId: 0,
  disableUserSelection: false,
  open: false,
};

export default TimeRecordTrackedItemModalFormContainer;
